import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTitle } from '../lib/useTitle.js';
import { useMetaTags } from '../lib/useMetaTags.js';
import PublicHeader from '../components/PublicHeader.jsx';
import Footer from '../components/Footer.jsx';

export default function Pricing() {
  useTitle('Pricing');
  useMetaTags({
    description: 'TrainForge pricing: free for individuals and gyms during beta, no credit card required. Beta partner gyms lock in founder pricing for life when paid plans launch.',
    ogTitle: 'TrainForge pricing | free during beta',
    ogDescription: 'Free for everyone during beta. Beta partner gyms lock in founder pricing for life.',
    ogType: 'website',
    canonical: 'https://mytrainforge.com/pricing',
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />

      <section style={{ padding: '48px 24px 32px', textAlign: 'center', maxWidth: 800, margin: '0 auto' }}>
        <h1 style={{ fontSize: 36, margin: '0 0 12px' }}>Simple, transparent pricing</h1>
        <p style={{ color: 'var(--text-dim)', fontSize: 17, margin: '0 0 8px', lineHeight: 1.5 }}>
          Free for everyone during beta. No credit card required.
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
          Paid plans launch when beta ends. Beta partners lock in founder pricing for life.
        </p>
      </section>

      <section style={{ padding: '0 24px 60px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          <TierCard
            name="Individual"
            price="Free"
            priceNote="Always free"
            desc="For anyone who wants to train smarter on their own."
            features={[
              { text: 'AI workout programs (3 drafts, compare & pick)', included: true },
              { text: 'Barcode scanner + AI food photo analysis', included: true },
              { text: 'Adaptive nutrition coaching', included: true },
              { text: 'Gym profiles (Planet Fitness, LA Fitness, etc.)', included: true },
              { text: '322 exercises with form cues', included: true },
              { text: '17 achievements + weekly streaks', included: true },
              { text: 'Session conversion between gyms', included: true },
              { text: 'Body measurements + progress photos', included: true },
              { text: 'Workout timers (EMOM, AMRAP, Tabata)', included: true },
              { text: '1RM + plate calculator', included: true },
              { text: 'White-label branding', included: false },
              { text: 'Member management', included: false },
              { text: 'Trainer tools', included: false },
            ]}
            cta="Get started free"
            ctaLink="/login"
          />
          <TierCard
            name="Gym Starter"
            price="Free during beta"
            priceNote="Paid plans coming soon"
            desc="For small gyms and independent personal trainers."
            highlighted
            features={[
              { text: 'Everything in Individual', included: true },
              { text: 'White-label branding (logo, colors, name)', included: true },
              { text: 'Custom subdomain (yourgym.mytrainforge.com)', included: true },
              { text: 'Up to 2 trainers', included: true },
              { text: 'Up to 50 members', included: true },
              { text: 'Equipment library (322 exercises, 60+ types)', included: true },
              { text: 'Member + trainer invite system', included: true },
              { text: 'In-app messaging', included: true },
              { text: 'Audit log', included: true },
              { text: 'Leaderboards', included: true },
              { text: 'Feature flags', included: false },
              { text: 'TV leaderboard mode', included: false },
            ]}
            cta="Request beta access"
            ctaLink="/contact"
          />
          <TierCard
            name="Gym Pro"
            price="Free during beta"
            priceNote="Paid plans coming soon"
            desc="For larger gyms, multi-location, and high-volume operations."
            features={[
              { text: 'Everything in Starter', included: true },
              { text: 'Unlimited trainers', included: true },
              { text: 'Unlimited members', included: true },
              { text: 'TV leaderboard mode', included: true },
              { text: 'Per-member feature flags', included: true },
              { text: 'Gamification + rewards + redemptions', included: true },
              { text: 'Compliance dashboard + risk flags', included: true },
              { text: 'Program adherence tracking', included: true },
              { text: 'Data export (CSV + GDPR JSON)', included: true },
              { text: 'Priority support', included: true },
              { text: 'Multi-gym management', included: true },
            ]}
            cta="Request beta access"
            ctaLink="/contact"
          />
        </div>
      </section>

      <section style={{ padding: '48px 24px', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', margin: '0 0 32px', fontSize: 28 }}>Pricing FAQ</h2>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <FaqItem q="When does the beta end?" a="We don't have a fixed date. We'll announce paid plans at least 30 days before they take effect. Beta partners will be grandfathered at founder pricing." />
            <FaqItem q="What is founder pricing?" a="Beta gyms that help us shape the product get a permanent discount when paid plans launch. The exact discount depends on how early you join and the feedback you provide." />
            <FaqItem q="Is the Individual tier really free forever?" a="Yes. We believe everyone should have access to quality training tools. The Individual tier will always be free with all core features included." />
            <FaqItem q="Can I upgrade from Starter to Pro later?" a="Absolutely. You'll be able to upgrade at any time without losing data. Member accounts, workout history, and all gym settings carry over." />
            <FaqItem q="What payment methods will you accept?" a="We plan to use Stripe for billing. Credit/debit cards, and potentially ACH for annual plans. Billing will be monthly or annual (with a discount)." />
          </div>
        </div>
      </section>

      <section style={{ padding: '60px 24px', background: 'var(--bg-elev)', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 28, margin: '0 0 12px' }}>Not sure which plan?</h2>
          <p style={{ color: 'var(--text-dim)', margin: '0 0 24px', fontSize: 16, lineHeight: 1.5 }}>
            Reach out and we'll help you figure out the best fit for your gym.
          </p>
          <Link to="/contact"><button className="primary" style={{ padding: '14px 28px', fontSize: 16 }}>Talk to us</button></Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TierCard({ name, price, priceNote, desc, features, highlighted, cta, ctaLink }) {
  return (
    <div style={{
      background: 'var(--bg-elev)',
      border: '1px solid ' + (highlighted ? 'var(--accent)' : 'var(--border)'),
      borderRadius: 'var(--radius-lg)',
      padding: 28,
      textAlign: 'left',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {highlighted && <div style={{ position: 'absolute', top: -10, right: 16, background: 'var(--accent)', color: 'var(--bg)', fontSize: 10, padding: '4px 10px', borderRadius: 4, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>Most popular</div>}
      <h3 style={{ margin: '0 0 4px', fontSize: 20 }}>{name}</h3>
      <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 2 }}>{price}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{priceNote}</div>
      <p style={{ fontSize: 14, color: 'var(--text-dim)', marginBottom: 20, lineHeight: 1.4 }}>{desc}</p>
      <ul style={{ margin: '0 0 24px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, flex: 1 }}>
        {features.map((f, i) => (
          <li key={i} style={{ paddingLeft: 20, position: 'relative', color: f.included ? 'var(--text-dim)' : 'var(--text-muted)' }}>
            <span style={{ position: 'absolute', left: 0, color: f.included ? 'var(--success)' : 'var(--text-muted)' }}>
              {f.included ? '✓' : '-'}
            </span>
            {f.text}
          </li>
        ))}
      </ul>
      <Link to={ctaLink}><button className={highlighted ? 'primary' : ''} style={{ width: '100%' }}>{cta}</button></Link>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', textAlign: 'left', padding: '16px 0', minHeight: 0,
          background: 'transparent', border: 'none', color: 'var(--text)', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
          fontSize: 15, fontWeight: 500,
        }}
      >
        <span>{q}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: 18, flexShrink: 0 }}>{open ? '-' : '+'}</span>
      </button>
      {open && <div style={{ padding: '0 0 16px', color: 'var(--text-dim)', fontSize: 14, lineHeight: 1.6 }}>{a}</div>}
    </div>
  );
}
